import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { clientsApi, subCompaniesApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Building2, Pencil, Trash2 } from 'lucide-react';

export default function OrganisationPage() {
  const [loading, setLoading] = useState(true);

  const [clients, setClients] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState('');

  // Dialog states
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [editSubOpen, setEditSubOpen] = useState(false);

  // Forms
  const [clientName, setClientName] = useState('');
  const [editingClient, setEditingClient] = useState(null);

  const [subName, setSubName] = useState('');
  const [editingSub, setEditingSub] = useState(null);

  const loadClients = async () => {
    const data = await clientsApi.getAll();
    setClients(data);
    return data;
  };

  const loadSubCompanies = async (clientId) => {
    if (!clientId) {
      setSubCompanies([]);
      return;
    }
    const data = await subCompaniesApi.getAll(clientId);
    setSubCompanies(data);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await loadClients();
      const defaultClientId = selectedClientId || data?.[0]?.client_id || '';
      setSelectedClientId(defaultClientId);
      await loadSubCompanies(defaultClientId);
    } catch (e) {
      toast.error('Failed to load organisation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadSubCompanies(selectedClientId);
  }, [selectedClientId]);

  const resetClientForm = () => setClientName('');
  const resetSubForm = () => setSubName('');

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const created = await clientsApi.create({ name: clientName });
      toast.success('Client created');
      setCreateClientOpen(false);
      resetClientForm();
      await loadClients();
      setSelectedClientId(created.client_id);
    } catch (err) {
      toast.error(err.message || 'Failed to create client');
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!editingClient) return;
    try {
      await clientsApi.update(editingClient.client_id, { name: clientName });
      toast.success('Client updated');
      setEditClientOpen(false);
      setEditingClient(null);
      resetClientForm();
      await loadClients();
    } catch (err) {
      toast.error(err.message || 'Failed to update client');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Delete this client? This will also remove its sub-companies and detach exercises.')) return;
    try {
      await clientsApi.delete(clientId);
      toast.success('Client deleted');
      if (selectedClientId === clientId) setSelectedClientId('');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete client');
    }
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setClientName(client.name || '');
    setEditClientOpen(true);
  };

  const handleCreateSub = async (e) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      await subCompaniesApi.create({ client_id: selectedClientId, name: subName });
      toast.success('Sub-company created');
      setCreateSubOpen(false);
      resetSubForm();
      await loadSubCompanies(selectedClientId);
    } catch (err) {
      toast.error(err.message || 'Failed to create sub-company');
    }
  };

  const openEditSub = (sub) => {
    setEditingSub(sub);
    setSubName(sub.name || '');
    setEditSubOpen(true);
  };

  const handleUpdateSub = async (e) => {
    e.preventDefault();
    if (!editingSub) return;
    try {
      await subCompaniesApi.update(editingSub.sub_company_id, { name: subName });
      toast.success('Sub-company updated');
      setEditSubOpen(false);
      setEditingSub(null);
      resetSubForm();
      await loadSubCompanies(selectedClientId);
    } catch (err) {
      toast.error(err.message || 'Failed to update sub-company');
    }
  };

  const handleDeleteSub = async (subCompanyId) => {
    if (!window.confirm('Delete this sub-company? Exercises will be moved to the main client.')) return;
    try {
      await subCompaniesApi.delete(subCompanyId);
      toast.success('Sub-company deleted');
      await loadSubCompanies(selectedClientId);
    } catch (err) {
      toast.error(err.message || 'Failed to delete sub-company');
    }
  };

  return (
    <DashboardLayout title="Organisation">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Client & Sub-Company Management</h2>
            <p className="text-sm text-muted-foreground">Super Admin only</p>
          </div>

          <Dialog open={createClientOpen} onOpenChange={setCreateClientOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-secondary">Create Client</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4">
                <div className="space-y-2">
                  <Label>Client Name</Label>
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)} required />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateClientOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-secondary text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Clients
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
              ) : clients.length === 0 ? (
                <div className="text-sm text-muted-foreground">No clients yet</div>
              ) : (
                <div className="space-y-2">
                  {clients.map((c) => (
                    <div
                      key={c.client_id}
                      className={`p-3 rounded-lg border flex items-center justify-between gap-3 ${
                        selectedClientId === c.client_id ? 'border-primary/40 bg-primary/5' : 'border-border'
                      }`}
                    >
                      <button
                        type="button"
                        className="flex-1 text-left"
                        onClick={() => setSelectedClientId(c.client_id)}
                      >
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.client_id}</div>
                      </button>

                      <div className="flex items-center gap-2">
                        <Button size="icon" variant="ghost" onClick={() => openEditClient(c)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteClient(c.client_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!!clients.length && (
                <div className="pt-2">
                  <Label className="text-xs">Selected client</Label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.client_id} value={c.client_id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-secondary text-lg">Sub-Companies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  {selectedClientId ? 'Manage sub-companies for selected client' : 'Select a client first'}
                </div>

                <Dialog open={createSubOpen} onOpenChange={setCreateSubOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" disabled={!selectedClientId}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Sub-Company
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-secondary">Create Sub-Company</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateSub} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Sub-Company Name</Label>
                        <Input value={subName} onChange={(e) => setSubName(e.target.value)} required />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setCreateSubOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">Create</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {selectedClientId && subCompanies.length === 0 && (
                <div className="text-sm text-muted-foreground">No sub-companies for this client</div>
              )}

              <div className="space-y-2">
                {subCompanies.map((s) => (
                  <div key={s.sub_company_id} className="p-3 rounded-lg border flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.sub_company_id}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="ghost" onClick={() => openEditSub(s)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteSub(s.sub_company_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Edit dialogs */}
        <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary">Edit Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateClient} className="space-y-4">
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} required />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditClientOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={editSubOpen} onOpenChange={setEditSubOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary">Edit Sub-Company</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateSub} className="space-y-4">
              <div className="space-y-2">
                <Label>Sub-Company Name</Label>
                <Input value={subName} onChange={(e) => setSubName(e.target.value)} required />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditSubOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
